<template>
  <main class="public-cms-page">
    <section class="public-cms-container">
      <div v-if="loading" class="public-cms-state" role="status">
        <div class="public-cms-spinner" aria-hidden="true"></div>
        <p>Loading {{ fallbackTitle }}...</p>
      </div>

      <div v-else-if="errorMessage" class="public-cms-state">
        <i class="fa fa-file-alt" aria-hidden="true"></i>
        <h1>{{ fallbackTitle }}</h1>
        <p>{{ errorMessage }}</p>
      </div>

      <article v-else>
        <header class="public-cms-header">
          <h1>{{ pageTitle }}</h1>
        </header>
        <div class="public-cms-content" v-html="pageContent"></div>
      </article>
    </section>
  </main>
</template>

<script>
const API_ENDPOINT = "cms-data/get-by-slug/";

export default {
  data() {
    return {
      errorMessage: "",
      loading: true,
      page: {},
    };
  },
  computed: {
    slug() {
      return this.$route.params.slug || "";
    },
    fallbackTitle() {
      return this.formatSlugTitle(this.slug);
    },
    pageTitle() {
      return (
        this.page.title ||
        this.page.page_title ||
        this.page.name ||
        this.fallbackTitle
      );
    },
    pageContent() {
      return (
        this.page.html ||
        this.page.content ||
        this.page.description ||
        this.page.body ||
        ""
      );
    },
  },
  watch: {
    slug() {
      this.loadPage();
    },
  },
  async mounted() {
    await this.loadPage();
  },
  methods: {
    async loadPage() {
      this.loading = true;
      this.errorMessage = "";
      this.page = {};

      try {
        const response = await this.$apiService.getCall(
          API_ENDPOINT + encodeURIComponent(this.slug)
        );

        if (response.isError || this.hasApiFailure(response.apidata)) {
          this.errorMessage = "This page is currently unavailable.";
          return;
        }

        const page = this.extractRecord(response.apidata);
        if (!page || !this.isPublished(page) || !this.hasContent(page)) {
          this.errorMessage = "This page has not been published yet.";
          return;
        }

        this.page = page;
      } finally {
        this.loading = false;
      }
    },
    extractRecord(apiData) {
      const candidates = [
        apiData && apiData.Data,
        apiData && apiData.data,
        apiData && apiData.cms,
        apiData && apiData.page,
        apiData && apiData.result,
      ];

      for (const item of candidates) {
        if (!item) continue;
        if (Array.isArray(item)) return item[0];
        if (item.page) return item.page;
        if (item.cms) return item.cms;
        return item;
      }

      return null;
    },
    isPublished(page) {
      if (page.active_status === false || page.is_active === false) return false;
      if (!page.status) return true;

      return page.status.toString().toLowerCase() === "active";
    },
    hasContent(page) {
      const html =
        page.html ||
        page.content ||
        page.description ||
        page.body ||
        "";

      return Boolean(
        html
          .replace(/<[^>]*>/g, "")
          .replace(/&nbsp;/g, "")
          .trim()
      );
    },
    hasApiFailure(apiData) {
      return apiData && apiData.Code && apiData.Code !== "P00001";
    },
    formatSlugTitle(slug) {
      if (!slug) return "CMS Page";

      return slug
        .split("-")
        .filter(Boolean)
        .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
        .join(" ");
    },
  },
};
</script>

<style scoped>
.public-cms-page {
  background: #000000;
  color: #ffffff;
  min-height: 100vh;
  padding: 56px 20px;
}

.public-cms-container {
  margin: 0 auto;
  max-width: 960px;
}

.public-cms-header {
  border-bottom: 1px solid #2b3553;
  margin-bottom: 28px;
  padding-bottom: 18px;
}

.public-cms-header h1 {
  color: #ffffff;
  font-size: clamp(28px, 5vw, 44px);
  margin: 0;
}

.public-cms-content {
  color: #ffffff;
  overflow-wrap: anywhere;
}

.public-cms-content >>> [data-cms-html-content] {
  min-height: auto !important;
  padding: 0 !important;
}

.public-cms-state {
  align-items: center;
  display: flex;
  flex-direction: column;
  justify-content: center;
  min-height: calc(100vh - 112px);
  text-align: center;
}

.public-cms-state i {
  color: #ba54f5;
  font-size: 42px;
  margin-bottom: 16px;
}

.public-cms-state h1 {
  color: #ffffff;
  margin-bottom: 8px;
}

.public-cms-state p {
  color: #adb5bd;
}

.public-cms-spinner {
  animation: public-cms-spin 0.8s linear infinite;
  border: 3px solid #2b3553;
  border-radius: 50%;
  border-top-color: #e14eca;
  height: 38px;
  margin-bottom: 14px;
  width: 38px;
}

@keyframes public-cms-spin {
  to {
    transform: rotate(360deg);
  }
}

@media (max-width: 575px) {
  .public-cms-page {
    padding: 36px 16px;
  }
}
</style>
